import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Container, createStyles, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';

import {
  AppColors,
  FormHub,
  getAcceptCardData,
  GuestForm,
  kioskStyles,
  LocationForm,
  LocationFormFields,
  PaymentDetailsForm,
  ReservationConfirmation,
  RoomRateForm,
  useBookingStatus,
  useCommonStyle,
  useCurrentUserId,
  useFormEvents,
  useObservable,
  usePageTitle,
  useRoomRates,
  withPropertyFeatures,
} from '@lib/common';
import {
  BookingRequest,
  bookingService,
  corporateAccountQuery,
  corporateAccountService,
  FormStatus,
  guestProfileQuery,
  guestProfileService,
  isReservationAvailableForCheckin,
  propertyQuery,
  ReservationForms,
  RoomRateForms,
  roomRateQuery,
  roomRateService,
  RoomType,
  roomTypeQuery,
  sessionQuery,
  ContactType,
} from '@lib/state';
import { forceLogoutOnBackAction } from 'app/shared/native-interface/nativebridge';
import { KioskFormLoginOptions } from '../components/kiosk-form-login-options';

const useStyles = makeStyles(
  createStyles({
    formField: {
      '& svg': {
        color: AppColors.LightBlue,
      },
    },
    paddedForm: {
      paddingTop: '1em !important',
      paddingBottom: '1em !important',
    },
  })
);

const CreateReservation: React.FC<RouteComponentProps> = ({ history }) => {
  const { kioskForm, formInstructions, containedListItem } = kioskStyles();
  const styles = useStyles();
  const commonStyles = useCommonStyle();
  usePageTitle('Get A Room');

  const [corporateAccountSelected, setCorporateAccountSelected] = useState<string | undefined>(
    undefined
  );
  const [roomType, setRoomType] = useState<RoomType | undefined>(undefined);
  const [editLocation, setEditLocation] = useState(true);
  const [requiresPayment, setRequiresPayment] = useState(true);
  const [locationFormError, setLocationFormError] = useState<Error | undefined>(undefined);

  const [{ error, status }, resetReservationFormStatus] = useFormEvents(
    ReservationForms.CreateReservation
  );
  const [{ error: availabilityError, status: availabilityStatus }] = useFormEvents(
    RoomRateForms.Availability
  );

  const propertyId = useObservable(sessionQuery.propertyId);
  const properties = useObservable(propertyQuery.properties);
  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);

  const isLoggedIn = useObservable(sessionQuery.isLoggedIn);
  const guest = useObservable(guestProfileQuery.guest);
  const myCorporateAccounts = useObservable(corporateAccountQuery.accounts);

  const userId = useCurrentUserId();
  const bookingStatus = useBookingStatus();

  const defaultRoomType = useMemo(() => {
    const lowestValue = Math.min(...roomTypes.map(r => r.sortOrder ?? 0));
    return roomTypes.find(r => r.sortOrder === lowestValue);
  }, [roomTypes]);

  const {
    uiState,
    roomRates,
    checkInStartOfDay,
    checkOutStartOfDay,
    checkInDefaultDate,
    corporateBooking,
    costCenterOptions,
    quantity,
    policy,
    guestsPerRoom,
    loading,
  } = useRoomRates(userId, corporateAccountSelected);

  const [didSelectDates, setDidSelectDates] = useState(
    !!(uiState.checkInDate && uiState.checkOutDate)
  );

  const roomTypeId = useMemo(() => {
    return uiState.roomTypeId;
  }, [uiState]);

  useEffect(() => {
    const roomType = roomTypes.find(r => r.id === roomTypeId);
    setRoomType(roomType);
  }, [roomTypeId, roomTypes]);

  const selectedRatePlan = useMemo(() => roomRates?.find(r => r.roomTypeId === roomType?.id), [
    roomRates,
    roomType,
  ]);

  const corporateAccountId = useMemo(() => {
    return corporateBooking?.id;
  }, [corporateBooking]);

  useEffect(() => {
    //reset the reservation form status when the quantity is changed
    resetReservationFormStatus();
  }, [quantity, resetReservationFormStatus]);

  useEffect(() => {
    if (isLoggedIn) {
      // make sure user is logged out on back action
      forceLogoutOnBackAction();
      // get guest profile
      guestProfileService.getGuestProfile();
      corporateAccountService.getMyAccounts();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (availabilityStatus === FormStatus.Success && didSelectDates) {
      setEditLocation(false);
    }
  }, [availabilityStatus, didSelectDates]);

  useEffect(() => {
    if (
      bookingStatus?.reservation?.userId &&
      isReservationAvailableForCheckin(bookingStatus.reservation)
    ) {
      history.push(`/kiosk/${bookingStatus.reservation.id}/check-in`);
    }
    // eslint-disable-next-line
  }, [bookingStatus?.reservation?.id]);

  const submitLocationForm = useCallback(
    (values: LocationFormFields) => {
      setCorporateAccountSelected(values.corporateAccountId ?? undefined);
      const newRoomType = roomTypes.find(r => r.id === values.roomTypeId);
      roomRateService.updateUI({ ...values, guestsPerRoom: newRoomType?.totalOccupancy ?? 1 });
      setDidSelectDates(true);
      resetReservationFormStatus();
      setLocationFormError(undefined);
    },
    [resetReservationFormStatus, roomTypes, setLocationFormError]
  );

  const submit = useCallback(
    ({ password, card, ...booking }: any) => {
      const reservationQuantity = !booking.guestTbd
        ? booking.quantity * guestsPerRoom
        : booking.quantity * guestsPerRoom - 1;

      const request: BookingRequest = {
        card: getAcceptCardData(card),
        ...booking,
        quantity: reservationQuantity,
        corporateAccountId: corporateAccountId,
        rooms: booking.guestTbd
          ? booking.rooms.filter(
              (r: { group: string }) => r.group !== reservationQuantity.toString()
            )
          : booking.rooms,
        fields: booking.fields?.filter((f: { key: string }) => f.key) ?? [],
      };

      if (userId) request.contact.userId = userId;
      if (uiState.promoCode) request.promoCode = uiState.promoCode;

      if (isLoggedIn) {
        bookingService.createReservation(
          request,
          !!corporateAccountSelected || request.contact.contactType === ContactType.Delegate
        );
      } else {
        bookingService.createAccountAndReservation(password, request);
      }
    },
    [uiState, isLoggedIn, userId, corporateAccountId, guestsPerRoom, corporateAccountSelected]
  );

  const onRateFormLoadFail = useCallback(() => {
    //Prevent from firing early
    if (!editLocation) {
      setLocationFormError(new Error('There was an error. Please try again.'));
      setEditLocation(true);
      setDidSelectDates(false);
    }
  }, [setEditLocation, setDidSelectDates, setLocationFormError, editLocation]);

  return (
    <Container maxWidth="lg" className={clsx(kioskForm, styles.paddedForm)}>
      <Typography
        variant="h4"
        color="textPrimary"
        align="center"
        paragraph
        className={clsx(formInstructions, { [commonStyles.hidden]: !editLocation })}
      >
        Select the dates for your stay.
      </Typography>
      {bookingStatus ? (
        <ReservationConfirmation
          reservations={bookingStatus.reservations ?? []}
          failures={bookingStatus.failures ?? []}
          consumedHolds={bookingStatus.consumedHolds}
          properties={properties}
          policy={policy}
        />
      ) : (
        <FormHub onSubmit={submit}>
          <LocationForm
            //Component props
            className={clsx({ [commonStyles.hidden]: !editLocation })}
            hideLocationForPropertyId
            itemSpacing={3}
            //Reservation details props
            properties={properties}
            corporateAccounts={myCorporateAccounts}
            itemClass={styles.formField}
            minCheckIn={checkInDefaultDate}
            roomTypes={roomTypes}
            defaultRoomTypeId={defaultRoomType?.id}
            defaultCheckIn={checkInStartOfDay}
            defaultCheckOut={checkOutStartOfDay}
            defaultPropertyId={propertyId}
            defaultQuantity={quantity}
            defaultCorporateAccountId={corporateAccountId}
            //Form props
            onStepComplete={submitLocationForm}
            status={availabilityStatus}
            error={availabilityError ?? locationFormError}
          />
          {!editLocation && quantity && roomRates?.length > 0 && (
            <>
              <RoomRateForm
                //Component props
                className={containedListItem}
                usePaper={false}
                //Reservation details props
                properties={properties}
                policy={policy}
                quantity={quantity * guestsPerRoom}
                selectedRate={selectedRatePlan}
                roomType={roomType}
                //Form props
                onEditLocation={() => {
                  setEditLocation(true);
                  setDidSelectDates(false);
                }}
                onLoadFail={onRateFormLoadFail}
                setRequiresPayment={setRequiresPayment}
                status={availabilityStatus}
                availabilityError={availabilityError ?? locationFormError}
                multipleCorpAccts={myCorporateAccounts.length > 1}
                isLoading={loading || availabilityStatus === FormStatus.Pending}
                isSingle={guestsPerRoom === 1}
              />
              <GuestForm
                guest={guest}
                quantity={quantity}
                corporateBooking={corporateBooking}
                costCenterOptions={costCenterOptions}
                guestCount={roomType?.adultOccupancy?.maxOccupancy ?? undefined}
                useRearCam={false}
                guestsPerRoom={guestsPerRoom}
              >
                {!isLoggedIn && <KioskFormLoginOptions history={history} />}
              </GuestForm>
              <PaymentDetailsForm
                status={status}
                error={error ?? availabilityError}
                requiresPayment={requiresPayment}
                variant="contained"
                corporateAccountId={corporateAccountId}
              />
            </>
          )}
        </FormHub>
      )}
    </Container>
  );
};

export const CreateReservationPage = withPropertyFeatures(
  CreateReservation,
  roomRateQuery.propertyId
);
